import { compact } from 'lodash-es';
import { toBigWithDefault } from '../utils';
import type { QuoteLeg } from './Quote';

const quoteLegRepresentativeOptionalKeys = [
  'Market',
  'MarketAccount',
  'QuoteStatus',
  'QuoteRequestRejectReason',
  'Text',
  'BidPx',
  'OfferPx',
  'PricingReference',
  'BidAmt',
  'OfferAmt',
  'TransactTime',
] as const satisfies readonly (keyof QuoteLeg)[];

type QuoteLegRepresentativeOptionalKeys = (typeof quoteLegRepresentativeOptionalKeys)[number];

/**
 * Per-symbol representative leg used by care-order and other leg-aware views.
 *
 * - Before any leg for a symbol has traded quantity, market-specific and pricing fields may be omitted.
 * - Once there is traded quantity for the symbol, the representative row uses the traded leg
 *   and includes market / market-account / pricing details.
 */
export type QuoteLegRepresentative = Omit<QuoteLeg, QuoteLegRepresentativeOptionalKeys> &
  Partial<Pick<QuoteLeg, QuoteLegRepresentativeOptionalKeys>>;

/**
 * Each market on a quote has one set of each leg. Before there is any traded quantity on a symbol we return a compacted
 * leg without market-specific and pricing fields. Once there is trading on the symbol, we return the full traded leg
 * (including market-specific and pricing fields).
 */
export function getQuoteLegRepresentatives(legs: QuoteLeg[] | undefined): QuoteLegRepresentative[] {
  if (!legs || legs.length === 0) {
    return [];
  }

  const legsBySymbol = new Map<string, QuoteLeg[]>();
  for (const leg of legs) {
    const current = legsBySymbol.get(leg.Symbol) ?? [];
    current.push(leg);
    legsBySymbol.set(leg.Symbol, current);
  }

  const representativeLegs = Array.from(legsBySymbol.entries()).map(([_, symbolLegs]) => {
    const representativeLeg = pickRepresentativeLeg(symbolLegs);
    if (!representativeLeg) {
      // Shouldn't happen
      return undefined;
    }

    if (toBigWithDefault(representativeLeg.TradedQty, 0).gt(0)) {
      return representativeLeg;
    }

    const legDetail = { ...representativeLeg };
    for (const key of quoteLegRepresentativeOptionalKeys) {
      delete legDetail[key];
    }
    return legDetail;
  });

  return compact(representativeLegs);
}

/** For each symbol we prefer the traded leg, otherwise keep the first available leg. */
function pickRepresentativeLeg(legs: QuoteLeg[]) {
  const withTradedQty = legs.find(leg => toBigWithDefault(leg.TradedQty, 0).gt(0));
  return withTradedQty ?? legs.at(0);
}
